import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ENTRY_TOOLBAR_BUTTONS } from '../../config/entryStatusConfig';
import type { EntryStatus } from '../../../models';

/** Contadores de entries por status */
export type StatusCounts = Record<string, number>;

export interface EntryStatusToolbarProps {
  /** Callback cuando cambia el status seleccionado */
  onStatusChange?: (status?: EntryStatus) => void;
  /** Contadores de entries por status */
  statusCounts?: StatusCounts;
  /** Si está cargando los contadores */
  isLoadingCounts?: boolean;
  /** Tab activo */
  active: string;
  /** Setter para tab activo */
  setActive: (key: string) => void;
}

/**
 * Componente EntryStatusToolbar
 *
 * Barra de pestañas para filtrar entries por estado.
 * Similar al StatusToolbar de Orders.
 */
export const EntryStatusToolbar = ({
  onStatusChange,
  statusCounts = {},
  isLoadingCounts = false,
  active,
  setActive,
}: EntryStatusToolbarProps) => {
  const { t } = useTranslation('merchandiseV3');

  // Calcular el total para "Todos"
  const totalCount = useMemo(() => {
    return Object.values(statusCounts).reduce((sum, count) => sum + count, 0);
  }, [statusCounts]);

  // Obtener el conteo para un status específico
  const getCountForStatus = (status?: EntryStatus): number | undefined => {
    if (status === undefined) {
      // "Todos" - mostrar total
      return totalCount;
    }
    return statusCounts[status];
  };

  // Formatear el conteo para mostrar
  const formatCount = (count: number | undefined): string => {
    if (count === undefined) return '';
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}k`;
    }
    return String(count);
  };

  return (
    <div
      className="flex bg-card-bg rounded-lg text-sm p-1 mb-4 overflow-x-auto scrollbar-hide light:border-card-border light:border"
      style={{ WebkitOverflowScrolling: 'touch' }}
    >
      {ENTRY_TOOLBAR_BUTTONS.map((btn) => {
        const count = getCountForStatus(btn.status);
        const countText = formatCount(count);

        return (
          <a
            key={btn.key}
            href={btn.link}
            onClick={(e) => {
              e.preventDefault();
              setActive(btn.key);
              if (onStatusChange) {
                onStatusChange(btn.status);
              }
            }}
            className={`px-3 py-1 rounded hover:bg-hover-press whitespace-nowrap flex items-center gap-1.5 ${
              active === btn.key ? 'bg-hover-press' : ''
            }`}
          >
            <span>{t(`toolbar.${btn.key}`)}</span>
            {countText && (
              <span
                className={`text-xs px-1.5 py-0.5 rounded-full min-w-[20px] text-center ${
                  active === btn.key
                    ? 'bg-primary/20 text-primary'
                    : 'bg-muted text-muted-foreground'
                } ${isLoadingCounts ? 'animate-pulse' : ''}`}
              >
                {countText}
              </span>
            )}
          </a>
        );
      })}
    </div>
  );
};

